import { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DollarSign, BedDouble, UtensilsCrossed, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { RevenueChart, BookingStatusChart } from '../components/reports/RevenueTab';
import { OccupancyBarChart, RoomStatusPie } from '../components/reports/OccupancyTab';
import { FnBChart, FnBSummary } from '../components/reports/FnBTab';

export default function Reports() {
  const [revenueData, setRevenueData] = useState(null);
  const [occupancyData, setOccupancyData] = useState(null);
  const [fnbData, setFnbData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAllReports = useCallback(async () => {
    try {
      const [revenueRes, occupancyRes, fnbRes] = await Promise.all([
        api.get('/reports/revenue'),
        api.get('/reports/occupancy'),
        api.get('/reports/fnb')
      ]);
      setRevenueData(revenueRes.data);
      setOccupancyData(occupancyRes.data);
      setFnbData(fnbRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllReports();
  }, [loadAllReports]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div>
      </div>
    );
  }

  return (
    <div data-testid="reports-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-900 font-['Manrope'] tracking-tight">Reports & Analytics</h1>
        <p className="text-stone-500 mt-1">Business insights and performance metrics</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white border border-stone-200 rounded-md p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-sage-50 rounded-md"><DollarSign className="w-5 h-5 text-sage-700" /></div>
            <span className="text-sm text-stone-500">Total Revenue</span>
          </div>
          <p className="text-2xl font-bold text-stone-900 font-['Manrope']">{formatCurrency(revenueData?.total_revenue || 0)}</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-md p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-emerald-50 rounded-md"><BedDouble className="w-5 h-5 text-emerald-700" /></div>
            <span className="text-sm text-stone-500">Occupancy Rate</span>
          </div>
          <p className="text-2xl font-bold text-stone-900 font-['Manrope']">{occupancyData?.summary?.occupancy_rate || 0}%</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-md p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-50 rounded-md"><Calendar className="w-5 h-5 text-blue-700" /></div>
            <span className="text-sm text-stone-500">Total Bookings</span>
          </div>
          <p className="text-2xl font-bold text-stone-900 font-['Manrope']">{revenueData?.bookings?.total || 0}</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-md p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-50 rounded-md"><UtensilsCrossed className="w-5 h-5 text-purple-700" /></div>
            <span className="text-sm text-stone-500">F&B Revenue</span>
          </div>
          <p className="text-2xl font-bold text-stone-900 font-['Manrope']">{formatCurrency(fnbData?.summary?.total_revenue || 0)}</p>
        </div>
      </div>

      <Tabs defaultValue="revenue" className="w-full">
        <TabsList className="bg-stone-100 p-1 mb-6">
          <TabsTrigger value="revenue" className="data-[state=active]:bg-white data-[state=active]:text-sage-700 px-6" data-testid="tab-revenue">
            <DollarSign className="w-4 h-4 mr-2" />Revenue
          </TabsTrigger>
          <TabsTrigger value="occupancy" className="data-[state=active]:bg-white data-[state=active]:text-sage-700 px-6" data-testid="tab-occupancy">
            <BedDouble className="w-4 h-4 mr-2" />Occupancy
          </TabsTrigger>
          <TabsTrigger value="fnb" className="data-[state=active]:bg-white data-[state=active]:text-sage-700 px-6" data-testid="tab-fnb">
            <UtensilsCrossed className="w-4 h-4 mr-2" />F&B Sales
          </TabsTrigger>
        </TabsList>

        <TabsContent value="revenue">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <RevenueChart data={revenueData} formatCurrency={formatCurrency} />
            <BookingStatusChart data={revenueData} formatCurrency={formatCurrency} />
          </div>
        </TabsContent>

        <TabsContent value="occupancy">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <OccupancyBarChart data={occupancyData} />
            <RoomStatusPie data={occupancyData} />
          </div>
        </TabsContent>

        <TabsContent value="fnb">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <FnBChart data={fnbData} formatCurrency={formatCurrency} />
            <FnBSummary data={fnbData} formatCurrency={formatCurrency} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
